import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';

import { AuthError } from '@/modules/auth/components/AuthError';
import { useLoginMutation } from '@/modules/auth/mutations/useLoginMutation';
import { Form } from '@/modules/forms/components/Form';
import { FormInput } from '@/modules/forms/components/FormInput';
import { Button } from '@/modules/ui/components/button/Button';

const formSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1, 'Password is required'),
});

type FormValues = z.infer<typeof formSchema>;

interface PasswordLoginProps {
  /** Shown under the button; the page owns it so SSO failures land there too. */
  error: string | null;
  onSuccess: () => void;
  onError: (error?: Error) => void;
}

export const PasswordLogin = ({
  error,
  onSuccess,
  onError,
}: PasswordLoginProps) => {
  const loginMutation = useLoginMutation();
  const [showPassword, setShowPassword] = useState(false);

  const onSubmit = (values: FormValues) => {
    loginMutation.mutate(values, {
      onSuccess,
      onError,
    });
  };

  const passwordToggle = (
    <button
      type="button"
      onClick={() => setShowPassword((value) => !value)}
      aria-label={showPassword ? 'Hide password' : 'Show password'}
      aria-pressed={showPassword}
      className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-md outline-none focus-visible:ring-[3px]"
    >
      {showPassword ? (
        <EyeOffIcon className="size-4" aria-hidden="true" />
      ) : (
        <EyeIcon className="size-4" aria-hidden="true" />
      )}
    </button>
  );

  return (
    // Validate on submit only: the email field is autofocused, so the default
    // onBlur mode would flag it as required when the user clicks an SSO button.
    <Form schema={formSchema} onSubmit={onSubmit} mode="onSubmit">
      <div className="space-y-4">
        <FormInput
          name="email"
          label="Email"
          type="email"
          placeholder="you@company.com"
          autoFocus
        />
        <FormInput
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          className="pr-10"
          endAdornment={passwordToggle}
        />

        <Button
          type="submit"
          variant="primary"
          loading={loginMutation.isPending}
          className="h-9 w-full rounded-md active:scale-[0.98]"
        >
          Sign in
        </Button>
        {error && <AuthError message={error} />}
      </div>
    </Form>
  );
};
